<template>
  <a-modal
    class="position_modal"
    v-model:open="visible"
    title="Select a position"
    @cancel="handleCancel"
    width="800px"
    :footer="null"
  >
    <div class="tips">Please select your prefered job position</div>
    <div class="container">
      <div v-if="props.isEdit" class="input_wrapper">
        <div class="input_with_btn">
          <a-input v-model:value="filterForm.word" placeholder="Enter job title" @input="onInput" />
          <span
            class="submit_btn"
            :class="{ submit_btn_active: isWordModified }"
            @click.prevent="handleClickModifyItem"
          >
            <img src="@/assets/images/comfirm.svg" alt="Search" />
          </span>
        </div>
        <ul v-if="showSuggestions" class="suggestions_list" ref="suggestionsRef">
          <li v-for="item in suggestions.slice(0, 10)" :key="item.job" @click="selectSuggestion(item)">
            <span class="skill">{{ item.job }}</span>
            <span class="position">{{ item.industry }}</span>
          </li>
        </ul>
      </div>
      <div class="industry_wrapper">
        <div
          v-for="item in categoryTree"
          :key="item.categoryId"
          class="position"
          :class="positionSelected == item.categoryName ? 'selected' : ''"
          @click="positionSelected = item.categoryName"
        >
          {{ item.categoryName }}
        </div>
      </div>
      <div class="positon_wrapper">
        <span v-for="item in selectedChildren" :key="item.categoryId" class="positon" @click="handleClickItem(item)">
          {{ item.categoryName }}
        </span>
      </div>
    </div>
  </a-modal>
  <a-modal
    class="position_modal"
    v-model:open="skillVisible"
    :title="selectedPositionName + ' Skill Mastery'"
    @cancel="handleSKillCancel"
    :footer="null"
  >
    <div class="tips">Your skill selection will be used to recommend more matching job positions for you.</div>
    <div class="skill_container">
      <div class="select_count">Multi-select({{ selectedSkillList.length }})</div>
      <div class="skill_wrapper">
        <span
          class="skill"
          :class="{ selected: selectedSkillList.some((skill) => skill.categoryId === item.categoryId) }"
          v-for="item in skillList"
          :key="item.categoryId"
          @click="handleClickSkillItem(item)"
        >
          {{ item.categoryName }}
          <CheckCircleFilled
            v-if="selectedSkillList.some((skill) => skill.categoryId == item.categoryId)"
            class="select_icon"
          />
        </span>
      </div>
      <div class="btn_wrapper">
        <a-button class="cancel_btn" @click="handleSKillCancel">Previous</a-button>
        <a-button
          class="confirm_btn"
          type="primary"
          @click.prevent="handleSubmitSkillItem"
          :disabled="isSubmitDisabled"
        >
          Done
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { CheckCircleFilled } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { ref, defineExpose, defineEmits, onMounted, computed, reactive, defineProps, watch } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const props = defineProps({
  isEdit: {
    type: Boolean,
  },
});

const showSuggestions = ref(false);
const suggestions = ref([]);
const autoCompleteOptions = ref([]);
const filterForm = reactive({
  word: '',
});
const initialWord = ref('');
const isWordModified = computed(() => filterForm.word.trim() !== initialWord.value.trim());
const onInput = () => {
  showSuggestions.value = true;
  suggestions.value = autoCompleteOptions.value.filter((option) =>
    option.job.toLowerCase().includes(filterForm.word.toLowerCase())
  );
};
const selectSuggestion = (item) => {
  filterForm.word = item.job + ' ' + item.industry + '\n';
  showSuggestions.value = false;
};
const getCategoryList = () => {
  autoCompleteOptions.value = [
    ...store.state.categoryList.jobList.map((item) => ({
      job: item.job,
      industry: item.industry,
    })),
    ...store.state.categoryList.skillList.map((skill) => ({
      job: skill,
      industry: '',
    })),
  ];
};

const visible = ref(false);
const emit = defineEmits(['select-item', 'modify-item']);

const handleCancel = () => {
  visible.value = false;
};

const isIndexSearch = ref(false);
const open = (type) => {
  if (type == 'isIndexSearch') {
    isIndexSearch.value = true;
  }
  initialWord.value = filterForm.word;
  visible.value = true;
};
const updateName = (name) => {
  filterForm.word = name;
  initialWord.value = name;
};

const isSubmitDisabled = ref(false);
const skillVisible = ref(false);
const skillList = ref([]);
const selectedPosition = ref();
const selectedPositionName = ref();
const handleSKillCancel = () => {
  skillVisible.value = false;
};
const handleClickItem = (item) => {
  if (item.grandchildren && item.grandchildren.length > 0 && isIndexSearch.value == false) {
    skillVisible.value = true;
    skillList.value = item.grandchildren;
    selectedSkillList.value = [];
    selectedPosition.value = item;
    selectedPositionName.value = item.categoryName;
  } else {
    emit('select-item', item);
    handleCancel();
  }
};
const selectedSkillList = ref([]);
const handleClickSkillItem = (item) => {
  if (!selectedSkillList.value.some((skill) => skill.categoryId === item.categoryId)) {
    selectedSkillList.value.push(item);
  } else {
    selectedSkillList.value = selectedSkillList.value.filter((skill) => skill.categoryId !== item.categoryId);
  }
};
const handleSubmitSkillItem = () => {
  emit('select-item', selectedPosition.value, selectedSkillList.value);
  handleSKillCancel();
  handleCancel();
};
const handleClickModifyItem = () => {
  if (filterForm.word.trim() === '') {
    message.error('Please enter a job title');
    return;
  }
  emit('modify-item', filterForm.word.trim());
  handleCancel();
};

const categoryTree = ref([]);
const positionSelected = ref('');
const selectedChildren = computed(() => {
  const found = categoryTree.value.find((item) => item.categoryName === positionSelected.value);
  return found && found.children ? found.children : [];
});
const getCategoryTree = () => {
  categoryTree.value = store.state.categoryTree;
  positionSelected.value = store.state.categoryTree.length > 0 ? store.state.categoryTree[0].categoryName : '';
};

watch(
  () => store.state.categoryTree,
  () => {
    getCategoryTree();
  },
  { immediate: true }
);
watch(
  () => store.state.categoryList,
  () => {
    getCategoryList();
  },
  { immediate: true }
);

const suggestionsRef = ref(null);
const handleClickOutside = (event) => {
  if (suggestionsRef.value && !suggestionsRef.value.contains(event.target)) {
    showSuggestions.value = false;
  }
};
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  getCategoryList();
});

defineExpose({
  open,
  updateName,
});
</script>

<style lang="less">
.position_modal {
  color: #05644f;
  font-size: 16px;
  .ant-modal-content {
    border-radius: 20px;
    padding: 32px;
  }
  .ant-modal-title {
    color: #05644f;
    font-size: 20px;
    font-weight: bold;
    line-height: normal;
  }
  .ant-modal-close-icon {
    color: #05644f;
  }
  .tips {
    font-size: 16px;
    font-weight: 300;
    color: #05644f;
  }
  .container {
    display: flex;
    padding-top: 20px;
    flex-wrap: wrap;
    .input_wrapper {
      width: 100%;
      margin-bottom: 20px;
      position: relative;
      .input_with_btn {
        display: flex;
        align-items: center;
        border: 1px solid rgba(5, 100, 79, 0.5);
        box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
        border-radius: 16px;
        padding: 0 5px 0 15px;
        background: #fff;
        height: 40px;
        .ant-input {
          color: #05644f;
          border: none;
          box-shadow: none;
          padding: 0;
          height: 100%;
          line-height: 40px;
          font-size: 14px;
          flex: 1;
          background: transparent;
          display: flex;
          align-items: center;
          &::placeholder {
            color: rgba(5, 100, 79, 0.5);
          }
        }
        .submit_btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #d0d0d0;
          color: #fff;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
          margin-top: 1px;
          img {
            width: 16px;
            height: 16px;
            display: block;
          }
          &.submit_btn_active {
            background: #05644f;
          }
        }
      }
      .suggestions_list {
        position: absolute;
        background: #ffffff;
        box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.3);
        border-radius: 8px;
        width: 100%;
        margin-top: 10px;
        z-index: 99;
        li {
          list-style: none;
          padding: 10px 20px;
          color: #05644f;
          font-size: 16px;
          cursor: pointer;
          &:hover {
            background: rgba(5, 100, 79, 0.1);
          }
          .position {
            margin-left: 20px;
            color: rgba(5, 100, 79, 0.5);
          }
        }
      }
    }
    .industry_wrapper {
      width: 180px;
      .position {
        border-radius: 4px;
        padding: 10px;
        margin-bottom: 10px;
        cursor: pointer;
        font-size: 16px;
        color: rgba(5, 100, 79, 0.5);
        border-left: 5px solid transparent;
        &:hover,
        &.selected {
          background: #cdedd6;
          font-weight: bold;
          color: #05644f;
          border-left: 5px solid rgba(5, 100, 79, 0.5);
        }
      }
    }
    .positon_wrapper {
      flex: 1;
      border: 1px solid #cdedd6;
      border-radius: 8px;
      padding: 30px;
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-content: flex-start;
      gap: 15px;
      .positon {
        border: 1px solid rgba(5, 100, 79, 0.5);
        padding: 5px 10px;
        font-size: 14px;
        color: #05644f;
        border-radius: 100px;
        cursor: pointer;
        &:hover {
          background: #cdedd6;
          color: #05644f;
          border-color: transparent;
        }
      }
    }
  }
  .skill_container {
    padding-top: 20px;
    .skill_wrapper {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-content: flex-start;
      gap: 10px;
      min-height: 100px;
      .skill {
        border-radius: 100px;
        padding: 5px 10px;
        border: 1px solid rgba(194, 94, 27, 0.5);
        color: #c25e1b;
        cursor: pointer;
        position: relative;
        &.selected {
          background: #fef2ea;
          border-color: transparent;
        }
        .select_icon {
          color: #f78335;
          font-size: 18px;
          position: absolute;
          top: -5px;
          right: -5px;
        }
      }
    }
    .select_count {
      font-size: 12px;
      color: rgba(61, 61, 61, 0.5);
      margin-bottom: 10px;
    }
    .btn_wrapper {
      display: flex;
      justify-content: space-between;
      width: 100%;
      gap: 20px;
    }
    .confirm_btn,
    .cancel_btn {
      background: #05644f;
      border-radius: 100px;
      color: #fff;
      font-size: 16px;
      font-weight: bold;
      line-height: normal;
      display: block;
      flex: 1;
      padding: 10px 20px;
      height: auto;
      margin-top: 20px;
      border: none;
      &.cancel_btn {
        background: rgba(5, 100, 79, 0.3);
        color: #fff;
      }
    }
  }
}

/* 真机窄屏：800px 固定宽弹窗改为流式宽度 */
@media (max-width: 640px) {
  .position_modal {
    width: calc(100vw - 24px) !important;
    top: 20px;
    .ant-modal-content {
      padding: 20px 16px;
    }
  }
}
</style>
